/**
 * SportIcon.jsx — Renders a sport icon.
 * Carrom uses a custom image; all other sports use emojis.
 *
 * Usage:
 *   <SportIcon sport="carrom" size={28} />
 *   <SportIcon sport="table_tennis" size={32} />
 *   getSportEmoji("badminton")  → "🏸" (string, for title/alt/non-JSX use)
 */

/**
 * SportIcon.jsx — Renders a sport icon.
 * Carrom + Pickleball use custom images; all other sports use emojis.
 */

export const SPORT_EMOJI = {
  table_tennis: '🏓',
  badminton:    '🏸',
  chess:        '♙',   // white pawn — smaller, light coloured
  carrom:       '🎯',      // fallback only — image used in JSX
  pickleball:   '🥒',      // fallback only — image used in JSX
  squash:       '🎾',
  cricket:      '🏏',
  football:     '⚽',
  default:      '🏅',
};

/** Sport codes that use a custom image file */
const SPORT_IMAGES = {
  carrom:     '/icons/carrom.png',
  pickleball: '/icons/pickleball.png',
  badminton:  '/icons/badminton.png',
};

export const SPORT_LABELS = {
  table_tennis: 'Table Tennis',
  badminton:    'Badminton',
  chess:        'Chess',
  carrom:       'Carrom',
  pickleball:   'Pickleball',
  squash:       'Squash',
  cricket:      'Cricket',
  football:     'Football',
};

/** Returns the emoji string for non-JSX usage (title, alt, data). */
export function getSportEmoji(sport) {
  return SPORT_EMOJI[sport] || SPORT_EMOJI.default;
}

/**
 * SportIcon component.
 * @param {string}  sport   - sport code e.g. "carrom"
 * @param {number}  size    - pixel size (default 24)
 * @param {string}  className - extra tailwind classes
 */
export default function SportIcon({ sport, size = 36, className = '' }) {
  const imgSrc = SPORT_IMAGES[sport];

  if (imgSrc) {
    return (
      <img
        src={imgSrc}
        alt={SPORT_LABELS[sport] || sport}
        width={size}
        height={size}
        className={`inline-block object-contain flex-shrink-0 ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }

  const emoji = SPORT_EMOJI[sport] || SPORT_EMOJI.default;
  // Chess pawn renders smaller than other emojis — scale it down a touch more
  const scale = sport === 'chess' ? 0.62 : 0.85;
  return (
    <span
      className={`inline-block leading-none flex-shrink-0 ${className}`}
      style={{ fontSize: size * scale }}
      role="img"
      aria-label={SPORT_LABELS[sport] || sport}
    >
      {emoji}
    </span>
  );
}
